import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable, type DropResult } from '@hello-pangea/dnd';
import { useAuth } from '../../hooks/useAuth';
import { useFavorites, type FavoriteList } from '../../hooks/useFavorites';
import { usePropertyStore } from '../../hooks/usePropertyStore';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import SaudiRiyalSymbol from '../../components/base/icons/SaudiRiyalSymbol';
import ListCard from '../../components/favorites/ListCard';
import CreateListModal from '../../components/favorites/CreateListModal';
import DeleteConfirmModal from '../../components/favorites/DeleteConfirmModal';
import RecentlySaved, { type RecentlySavedProperty } from '../../components/favorites/RecentlySaved';
import PromoRail from '../../components/feature/PromoRail';
import EmptyFavorites from '../../components/favorites/EmptyFavorites';
import { apiRequest } from '../../utils/apiClient';
import { formatAmount } from '../../utils/numberFormat';
import { GooeyLoader } from '../../components/ui/gooey-loader';

// ============================================================================
// Types
// ============================================================================

interface FavoritePropertyDetails {
  id: string;
  propertyId: string;
  title: string;
  city: string;
  state: string;
  price: number;
  imageUrl: string | null;
  createdAt: string;
}

// ============================================================================
// Constants
// ============================================================================

// The swatches a user picks from when naming a favourites list.
//
// This was a generic "fun" palette - coral, turquoise, lemon, lime, orange,
// hot pink - which is a rainbow sitting inside a deep-teal site, and the one
// place a user could paint something genuinely off-brand onto their own page.
//
// These eight are all from the brand system and still read as eight distinct
// labels: three teals, two cyans, two golds, one red - the board's four
// colours, spread. Contrast is not a constraint here (a swatch carries only a
// decorative `text-white/30` icon on the cover, never copy), so the full-
// strength gold and cyan are allowed, which they would not be as foregrounds.
const LIST_COLORS = [
  '#004D40', '#1B7566', '#3C9F8F', '#007FAF', '#00AEEF', '#B58A0A', '#FFC107', '#DF2F2B'
];

const LIST_ICONS = [
  'ri-heart-line', 'ri-star-line', 'ri-bookmark-line', 'ri-home-heart-line',
  'ri-map-pin-line', 'ri-building-line', 'ri-key-line', 'ri-eye-line'
];

const LIST_ORDER_STORAGE_KEY = 'safaqat24.favorites.listOrder';

// ============================================================================
// Component
// ============================================================================

export default function FavoritesPage() {
  const { t, i18n } = useTranslation(['favorites', 'common']);
  const navigate = useNavigate();
  const { user, session } = useAuth();
  const {
    favorites,
    lists,
    loading: favoritesLoading,
    removeFavorite,
    createList,
    updateList,
    deleteList,
    getFavoritesInList,
    defaultListId
  } = useFavorites();
  const { getPropertyById: getLocalPropertyById } = usePropertyStore();

  const isRTL = i18n.dir() === 'rtl';

  // Selected list for viewing properties
  const [selectedListId, setSelectedListId] = useState<string | null>(null);

  // Property details for display
  const [favoriteProperties, setFavoriteProperties] = useState<FavoritePropertyDetails[]>([]);
  const [loadingProperties, setLoadingProperties] = useState(false);
  const [removingFavorite, setRemovingFavorite] = useState<string | null>(null);

  // List order for drag-and-drop (stored in localStorage)
  const [listOrder, setListOrder] = useState<string[]>([]);

  // Create/Edit modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingList, setEditingList] = useState<FavoriteList | null>(null);

  // Delete confirmation modal state
  const [deletingListId, setDeletingListId] = useState<string | null>(null);

  // ============================================================================
  // Auth Redirect
  // ============================================================================

  useEffect(() => {
    if (!user && !favoritesLoading) {
      navigate('/signin', { state: { from: { pathname: '/favorites' } } });
    }
  }, [user, favoritesLoading, navigate]);

  // ============================================================================
  // List Order Management
  // ============================================================================

  // Initialize list order from localStorage or from lists
  useEffect(() => {
    if (lists.length === 0) return;

    try {
      const storedOrder = localStorage.getItem(LIST_ORDER_STORAGE_KEY);
      if (storedOrder) {
        const parsed = JSON.parse(storedOrder) as string[];
        // Filter to only include IDs that exist in current lists
        const existingIds = new Set(lists.map(l => l.id));
        const validOrder = parsed.filter(id => existingIds.has(id));
        // Add any new lists not in stored order
        const newIds = lists.map(l => l.id).filter(id => !validOrder.includes(id));
        const fullOrder = [...validOrder, ...newIds];
        setListOrder(fullOrder);
      } else {
        // Initialize from current lists order
        setListOrder(lists.map(l => l.id));
      }
    } catch {
      // Fallback to current lists order
      setListOrder(lists.map(l => l.id));
    }
  }, [lists]);

  // Ordered lists based on listOrder
  const orderedLists = useMemo(() => {
    if (listOrder.length === 0) return lists;

    const orderMap = new Map(listOrder.map((id, index) => [id, index]));
    return [...lists].sort((a, b) => {
      const aIndex = orderMap.get(a.id) ?? Infinity;
      const bIndex = orderMap.get(b.id) ?? Infinity;
      return aIndex - bIndex;
    });
  }, [lists, listOrder]);

  // Handle drag end for reordering
  const handleDragEnd = useCallback((result: DropResult) => {
    if (!result.destination) return;
    if (result.source.index === result.destination.index) return;

    const reordered = Array.from(listOrder);
    const [removed] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, removed);

    setListOrder(reordered);
    localStorage.setItem(LIST_ORDER_STORAGE_KEY, JSON.stringify(reordered));
  }, [listOrder]);

  // ============================================================================
  // Cover Image Helper
  // ============================================================================

  const getCoverImage = useCallback((list: FavoriteList): string | null => {
    if (list.propertyIds.length === 0) return null;

    const firstPropertyId = list.propertyIds[0];
    const favoriteProperty = favoriteProperties.find(
      p => p.propertyId === firstPropertyId
    );

    return favoriteProperty?.imageUrl || null;
  }, [favoriteProperties]);

  // ============================================================================
  // Recent Properties (sorted by createdAt, limited to 5)
  // ============================================================================

  const recentProperties = useMemo((): RecentlySavedProperty[] => {
    return favoriteProperties
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .slice(0, 5)
      .map(p => ({
        id: p.id,
        propertyId: p.propertyId,
        title: p.title,
        imageUrl: p.imageUrl,
        createdAt: p.createdAt
      }));
  }, [favoriteProperties]);

  // ============================================================================
  // Fetch Property Details
  // ============================================================================

  const fetchFavoriteProperties = useCallback(async () => {
    if (favorites.length === 0) {
      setFavoriteProperties([]);
      return;
    }

    setLoadingProperties(true);
    try {
      const allPropertyIds = new Set(favorites.map(f => f.propertyId));
      const propertiesWithDetails: FavoritePropertyDetails[] = [];
      const foundPropertyIds = new Set<string>();

      // Try to get properties from backend first
      if (session?.token) {
        try {
          const response = await apiRequest<{
            data: Array<{
              id: string;
              propertyId: string;
              createdAt: string;
              property?: {
                id: string;
                title: string;
                titleAr?: string;
                price: number;
                images?: Array<{ url: string }>;
                city?: { nameEn: string; nameAr: string };
                area?: { nameEn: string; nameAr: string };
              };
            }>;
          }>('/favorites?page=1&pageSize=100', { token: session.token });

          const favoritesData = response.data || [];

          for (const fav of favoritesData) {
            if (fav.property && allPropertyIds.has(fav.propertyId)) {
              const prop = fav.property;
              const cityName = i18n.language === 'ar' ? prop.city?.nameAr : prop.city?.nameEn;
              const areaName = i18n.language === 'ar' ? prop.area?.nameAr : prop.area?.nameEn;
              const imageUrl = prop.images && prop.images.length > 0 ? prop.images[0].url : null;

              propertiesWithDetails.push({
                id: fav.id,
                propertyId: fav.propertyId,
                title: i18n.language === 'ar' && prop.titleAr ? prop.titleAr : prop.title || '',
                city: cityName || '',
                state: areaName || '',
                price: prop.price || 0,
                imageUrl,
                createdAt: fav.createdAt
              });
              foundPropertyIds.add(fav.propertyId);
            }
          }
        } catch (apiError) {
          console.warn('Failed to fetch from backend, using local fallback:', apiError);
        }
      }

      // Fallback: For any properties not found in backend, check local store
      for (const fav of favorites) {
        if (!foundPropertyIds.has(fav.propertyId)) {
          const localProperty = getLocalPropertyById(fav.propertyId);
          if (localProperty) {
            propertiesWithDetails.push({
              id: fav.id || fav.propertyId,
              propertyId: fav.propertyId,
              title: localProperty.title || '',
              city: localProperty.city || '',
              state: localProperty.state || '',
              price: localProperty.price || 0,
              imageUrl: localProperty.imageUrl || null,
              createdAt: fav.createdAt || new Date().toISOString()
            });
            foundPropertyIds.add(fav.propertyId);
          }
        }
      }

      setFavoriteProperties(propertiesWithDetails);
    } catch (error) {
      console.error('Failed to fetch favorite properties:', error);
      setFavoriteProperties([]);
    } finally {
      setLoadingProperties(false);
    }
  }, [favorites, session?.token, i18n.language, getLocalPropertyById]);

  useEffect(() => {
    if (user) {
      fetchFavoriteProperties();
    }
  }, [user, favorites, fetchFavoriteProperties]);

  // ============================================================================
  // Selected List Properties
  // ============================================================================

  const selectedListProperties = useMemo(() => {
    if (!selectedListId) return [];
    const listFavorites = getFavoritesInList(selectedListId);
    const listPropertyIds = new Set(listFavorites.map(f => f.propertyId));
    return favoriteProperties.filter(p => listPropertyIds.has(p.propertyId));
  }, [selectedListId, getFavoritesInList, favoriteProperties]);

  const selectedList = selectedListId ? lists.find(l => l.id === selectedListId) : null;

  // ============================================================================
  // Handlers
  // ============================================================================

  const handleRemoveFavorite = async (propertyId: string) => {
    setRemovingFavorite(propertyId);
    try {
      await removeFavorite(propertyId);
    } catch (error) {
      console.error('Failed to remove favorite:', error);
    } finally {
      setRemovingFavorite(null);
    }
  };

  const handleListClick = (listId: string) => {
    // Navigate to list detail page
    navigate(`/favorites/${listId}`);
  };

  const handleBackToLists = () => {
    setSelectedListId(null);
  };

  const openCreateModal = () => {
    setEditingList(null);
    setShowCreateModal(true);
  };

  const openEditModal = (list: FavoriteList) => {
    setEditingList(list);
    setShowCreateModal(true);
  };

  const handleCreateOrUpdateList = async (name: string, color: string, icon: string) => {
    if (editingList) {
      await updateList(editingList.id, { name, color, icon });
    } else {
      await createList(name, undefined, color, icon);
    }
  };

  const handleDeleteList = async () => {
    if (!deletingListId) return;
    await deleteList(deletingListId);
    setDeletingListId(null);
    if (selectedListId === deletingListId) {
      setSelectedListId(null);
    }
  };

  const deletingList = deletingListId ? lists.find(l => l.id === deletingListId) : null;

  // ============================================================================
  // Render Guards
  // ============================================================================

  if (!user) {
    return null;
  }

  const hasFavorites = favorites.length > 0;

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <div className="min-h-screen bg-[var(--pv-color-surface)]" dir={i18n.dir()}>
      <Navigation />

      <main className="pb-20">
        {/* Header */}
        <section className="bg-[#004D40] py-12">
          <div className="max-w-6xl mx-auto px-4">
            <div className={`flex items-center gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <div className="w-16 h-16 rounded-2xl bg-white/20 flex items-center justify-center">
                <i className="ri-heart-3-fill text-3xl text-white"></i>
              </div>
              <div className={isRTL ? 'text-right' : ''}>
                <h1 className="text-3xl font-bold text-white mb-1">
                  {t('favorites:favorites.title')}
                </h1>
                <p className="text-[#DDEEEB]">
                  {t('favorites:favorites.subtitle', {
                    count: favorites.length,
                    listCount: lists.length
                  })}
                </p>
              </div>
            </div>
          </div>
        </section>

        <div className="max-w-6xl mx-auto px-4 py-8">
          {/* Show EmptyFavorites when no favorites at all */}
          {!hasFavorites && !favoritesLoading && !loadingProperties && (
            <EmptyFavorites />
          )}

          {/* Show loading state */}
          {(favoritesLoading || loadingProperties) && !hasFavorites && (
            <div className="flex items-center justify-center py-16">
              <div className="text-center">
                <GooeyLoader primaryColor="#004D40" secondaryColor="#3C9F8F" borderColor="#E0E9E7" />
                <p className="text-gray-500">{t('favorites:favorites.loading')}</p>
              </div>
            </div>
          )}

          {/* Main content when favorites exist */}
          {hasFavorites && (
            <>
              {/* Recently Saved Section (only when not viewing a specific list) */}
              {!selectedListId && recentProperties.length > 0 && (
                <div className="mb-8">
                  <RecentlySaved properties={recentProperties} />
                </div>
              )}

              {/* Lists Grid (when no list is selected) */}
              {!selectedListId && (
                <div className="mb-8">
                  <div className={`flex items-center justify-between mb-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <h2 className={`text-lg font-semibold text-[var(--pv-color-primary)] ${isRTL ? 'text-right' : ''}`}>
                      {t('favorites:favorites.lists.title')}
                    </h2>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={openCreateModal}
                      className="flex items-center gap-2"
                    >
                      <i className="ri-add-line"></i>
                      {t('favorites:favorites.lists.newList')}
                    </Button>
                  </div>

                  {/* Draggable Lists Grid */}
                  <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId="lists" direction="horizontal">
                      {(provided, droppableSnapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          className={`grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 transition-colors duration-200 ${
                            droppableSnapshot.isDraggingOver ? 'bg-[var(--pv-color-surface-sunken)]/50 rounded-xl p-2 -m-2' : ''
                          }`}
                        >
                          {orderedLists.map((list, index) => (
                            <Draggable key={list.id} draggableId={list.id} index={index}>
                              {(provided, snapshot) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  className={`transition-transform duration-200 ease-out ${
                                    snapshot.isDragging ? 'z-50' : ''
                                  }`}
                                  style={{
                                    ...provided.draggableProps.style,
                                    // Smooth transition for non-dragging items
                                    transition: snapshot.isDragging
                                      ? provided.draggableProps.style?.transition
                                      : 'transform 200ms ease-out',
                                  }}
                                >
                                  <ListCard
                                    list={list}
                                    isDefault={list.id === defaultListId}
                                    coverImageUrl={getCoverImage(list)}
                                    onClick={() => handleListClick(list.id)}
                                    onEdit={list.id !== defaultListId ? () => openEditModal(list) : undefined}
                                    onDelete={list.id !== defaultListId ? () => setDeletingListId(list.id) : undefined}
                                    isDragging={snapshot.isDragging}
                                    dragHandleProps={provided.dragHandleProps ?? undefined}
                                  />
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                </div>
              )}

              {/* Selected List View */}
              {selectedListId && selectedList && (
                <div className="mb-8">
                  {/* Back Button and List Header */}
                  <div className={`flex items-center gap-4 mb-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <button
                      onClick={handleBackToLists}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <i className={`ri-arrow-${isRTL ? 'right' : 'left'}-line text-xl text-gray-600`}></i>
                    </button>
                    <div className={`flex-1 ${isRTL ? 'text-right' : ''}`}>
                      <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse justify-end' : ''}`}>
                        <div
                          className="w-10 h-10 rounded-xl flex items-center justify-center"
                          style={{ backgroundColor: selectedList.color || '#004D40' }}
                        >
                          <i className={`${selectedList.icon || 'ri-heart-line'} text-xl text-white`}></i>
                        </div>
                        <div>
                          <h2 className="text-lg font-semibold text-[var(--pv-color-primary)]">
                            {selectedList.name}
                          </h2>
                          <p className="text-sm text-gray-500">
                            {t('favorites:favorites.lists.propertyCount', { count: selectedListProperties.length })}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* List Properties Grid */}
                  {selectedListProperties.length === 0 ? (
                    <div className="text-center py-16 bg-gray-50 rounded-2xl border border-gray-200">
                      <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[var(--pv-color-surface-sunken)] flex items-center justify-center">
                        <i className="ri-heart-line text-4xl text-[#1B7566]"></i>
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">
                        {t('favorites:favorites.empty.listEmpty')}
                      </h3>
                      <p className="text-gray-500 mb-6 max-w-md mx-auto">
                        {t('favorites:favorites.empty.description')}
                      </p>
                      <Link to="/search">
                        <Button variant="primary">
                          <i className={`ri-search-line ${isRTL ? 'ml-2' : 'mr-2'}`}></i>
                          {t('favorites:favorites.empty.cta')}
                        </Button>
                      </Link>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                      {selectedListProperties.map((property) => (
                        <div
                          key={property.propertyId}
                          className="group bg-white rounded-2xl border border-gray-200 overflow-hidden hover:shadow-lg hover:border-[#1B7566]/40 transition-all"
                        >
                          {/* Image */}
                          <div className="relative h-48 bg-gray-100">
                            {property.imageUrl ? (
                              <img
                                src={property.imageUrl}
                                alt={property.title}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <i className="ri-home-line text-4xl text-gray-300"></i>
                              </div>
                            )}

                            {/* Price Badge */}
                            <div className="absolute top-3 left-3 flex items-center gap-1.5 bg-[#004D40] text-white px-3 py-1.5 rounded-lg text-sm font-semibold">
                              <SaudiRiyalSymbol className="w-4 h-4" />
                              {formatAmount(property.price)}
                            </div>

                            {/* Remove Button */}
                            <button
                              onClick={() => handleRemoveFavorite(property.propertyId)}
                              disabled={removingFavorite === property.propertyId}
                              className={`absolute top-3 right-3 w-9 h-9 rounded-full bg-white shadow-lg flex items-center justify-center transition-all ${
                                removingFavorite === property.propertyId
                                  ? 'opacity-50 cursor-wait'
                                  : 'hover:bg-red-50 text-red-500'
                              }`}
                            >
                              {removingFavorite === property.propertyId ? (
                                <i className="ri-loader-4-line animate-spin"></i>
                              ) : (
                                <i className="ri-heart-fill text-lg"></i>
                              )}
                            </button>
                          </div>

                          {/* Content */}
                          <div className="p-4">
                            <h4 className={`font-semibold text-gray-900 mb-2 line-clamp-1 group-hover:text-[#004D40] transition-colors ${isRTL ? 'text-right' : ''}`}>
                              {property.title}
                            </h4>
                            <p className={`text-sm text-gray-500 mb-4 flex items-center gap-1.5 ${isRTL ? 'flex-row-reverse text-right' : ''}`}>
                              <i className="ri-map-pin-line text-[#004D40]"></i>
                              {property.city}{property.state ? `, ${property.state}` : ''}
                            </p>

                            <Link
                              to={`/property/${property.propertyId}`}
                              className={`flex items-center justify-center gap-2 w-full py-2.5 rounded-xl text-sm font-medium text-[#004D40] bg-[var(--pv-color-surface-sunken)] hover:bg-[var(--pv-color-surface-sunken)] transition-colors ${isRTL ? 'flex-row-reverse' : ''}`}
                            >
                              <i className="ri-eye-line"></i>
                              {t('favorites:favorites.actions.viewDetails')}
                            </Link>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {/* Self-advertising band — relevant next steps for a signed-in user. */}
          <PromoRail surface="favorites" max={2} variant="band" className="mt-10" />
        </div>
      </main>

      {/* Create/Edit List Modal */}
      <CreateListModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreateOrUpdateList}
        editingList={editingList}
        colors={LIST_COLORS}
        icons={LIST_ICONS}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={!!deletingListId}
        onClose={() => setDeletingListId(null)}
        onConfirm={handleDeleteList}
        listName={deletingList?.name || ''}
      />

      <Footer />
    </div>
  );
}
